import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPersonnalisation } from '../services/api';

/**
 * Nuance dérivée d'une couleur de base.
 *
 * Tailwind attend onze crans, de 50 à 950 ; le comité n'en choisit qu'un. Les
 * autres se déduisent en tirant vers le blanc pour les clairs et vers le noir
 * pour les foncés. Le résultat n'égale pas une palette dessinée à la main,
 * mais il reste lisible sur toute l'échelle, ce qui est le seul contrat que le
 * reste du site attend.
 */
function melanger(hex: string, vers: [number, number, number], part: number): string {
  const base = [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];

  const canal = base.map((c, i) => Math.round(c + (vers[i] - c) * part));

  return `#${canal.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

const BLANC: [number, number, number] = [255, 255, 255];
const NOIR: [number, number, number] = [0, 0, 0];

/** La couleur choisie occupe le cran 600, celui des boutons et des liens. */
const ECHELLE: [number, number][] = [
  [50, 0.95],
  [100, 0.9],
  [200, 0.78],
  [300, 0.62],
  [400, 0.4],
  [500, 0.18],
  [600, 0],
  [700, -0.15],
  [800, -0.3],
  [900, -0.45],
  [950, -0.65],
];

export function palette(hex: string): Record<number, string> {
  const nuances: Record<number, string> = {};

  for (const [cran, part] of ECHELLE) {
    nuances[cran] =
      part === 0 ? hex : melanger(hex, part > 0 ? BLANC : NOIR, Math.abs(part));
  }

  return nuances;
}

const VALIDE = /^#[0-9a-fA-F]{6}$/;

/**
 * Applique les couleurs choisies par le comité à l'ensemble du site.
 *
 * Tailwind compile ses utilitaires en `var(--color-primary-600)` : redéfinir
 * ces variables sur la racine repeint tout, sans qu'aucun composant ait à
 * connaître le thème. Rien n'est appliqué tant que la couleur n'a pas été
 * lue — les valeurs de la feuille de style tiennent lieu de défaut, et
 * l'écran ne clignote pas.
 */
export function usePersonnalisation() {
  const { data } = useQuery({
    queryKey: ['personnalisation'],
    queryFn: fetchPersonnalisation,
    staleTime: 5 * 60 * 1000,
    // Un échec ponctuel laissait le site sur ses valeurs de repli — « notre
    // association » en page d'accueil — jusqu'au prochain rechargement : on
    // retente une fois avant de s'y résoudre.
    retry: 1,
  });

  const primaire = data?.personnalisation?.couleurPrimaire;
  const secondaire = data?.personnalisation?.couleurSecondaire;

  useEffect(() => {
    const racine = document.documentElement;

    for (const [nom, hex] of [
      ['primary', primaire],
      ['secondary', secondaire],
    ] as const) {
      if (typeof hex !== 'string' || !VALIDE.test(hex)) continue;

      for (const [cran, valeur] of Object.entries(palette(hex))) {
        racine.style.setProperty(`--color-${nom}-${cran}`, valeur);
      }
    }
  }, [primaire, secondaire]);

  return data?.personnalisation ?? null;
}
